import React, { useState, useEffect } from 'react';
import { 
  Facebook, 
  Instagram, 
  Youtube, 
  Twitter, 
  Video, 
  Users,
  TrendingUp,
  Sparkles,
  CheckCircle
} from 'lucide-react';

const PlatformInfo = () => {
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);

  // Function to extract platform ID from name
  const getPlatformId = (name) => {
    const idMap = {
      'Meta (Facebook/Instagram)': 'meta',
      'TikTok': 'tiktok',
      'Instagram': 'instagram',
      'YouTube': 'youtube',
      'Twitter/X': 'twitter'
    };
    return idMap[name] || 'unknown';
  };

  useEffect(() => {
    const fetchPlatforms = async () => {
      try {
        const response = await fetch('/api/platforms');
        const data = await response.json();
        setPlatforms(data.platforms);
      } catch (error) {
        console.error('Error fetching platforms:', error);
        // Show error state
        setPlatforms([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPlatforms();
  }, []);

  const getPlatformIcon = (platformId) => {
    const icons = {
      meta: <Facebook className="w-6 h-6" />,
      tiktok: <Video className="w-6 h-6" />,
      instagram: <Instagram className="w-6 h-6" />,
      youtube: <Youtube className="w-6 h-6" />,
      twitter: <Twitter className="w-6 h-6" />
    };
    return icons[platformId] || <Video className="w-6 h-6" />;
  };

  const getPlatformColor = (platformId) => {
    const colors = {
      meta: 'bg-blue-500',
      tiktok: 'bg-black',
      instagram: 'bg-pink-500',
      youtube: 'bg-red-500',
      twitter: 'bg-blue-400'
    };
    return colors[platformId] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <section className="max-w-6xl mx-auto mb-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading platform information...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-6xl mx-auto mb-16 mt-20">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Supported Platforms
        </h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Our AI analyzes your videos based on platform-specific best practices 
          and industry standards for maximum engagement and performance.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {platforms.map((platform) => {
          const platformId = getPlatformId(platform.name);
          return (
            <div key={platform.name} className="card hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center mb-6">
                <div className={`flex items-center justify-center w-12 h-12 ${getPlatformColor(platformId)} text-white rounded-lg mr-4`}>
                  {getPlatformIcon(platformId)}
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{platform.name}</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                    <TrendingUp className="w-4 h-4 mr-2 text-primary-600" />
                    Analysis Criteria
                  </h4>
                  <ul className="space-y-1">
                    {platform.criteria && platform.criteria.slice(0, 3).map((practice, index) => (
                      <li key={index} className="flex items-start text-sm text-gray-600">
                        <CheckCircle className="w-3 h-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        {practice}
                      </li>
                    ))}
                    {platform.criteria && platform.criteria.length > 3 && (
                      <li className="text-sm text-gray-500 ml-5">
                        +{platform.criteria.length - 3} more criteria
                      </li>
                    )}
                  </ul>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                    <Sparkles className="w-4 h-4 mr-2 text-yellow-600" />
                    Best Practices
                  </h4>
                  <ul className="space-y-1">
                    {platform.bestPractices && platform.bestPractices.slice(0, 3).map((practice, index) => (
                      <li key={index} className="flex items-start text-sm text-gray-600">
                        <div className="w-2 h-2 bg-primary-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                        {practice}
                      </li>
                    ))}
                    {platform.bestPractices && platform.bestPractices.length > 3 && (
                      <li className="text-sm text-gray-500 ml-4">
                        +{platform.bestPractices.length - 3} more practices
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Additional Info */}
      <div className="mt-16 text-center">
       
      </div>
    </section>
  );
};

export default PlatformInfo;
